import { useCallback, useEffect } from 'react'
import { useFunctions } from 'reactfire'
import { api } from './api'
import { Button, message, theme } from 'antd'
import { appVersion } from './general'
import { UpCircleFilled } from '@ant-design/icons'
import i18n from './i18n'

/**
 * Compare two version strings (e.g., "v1.0.10" VS "v1.0.8")
 * Returns true if `newVersion` is semantically newer than `currentVersion`
 */
function isNewerVersion(newVersion, currentVersion) {
  // Handle empty or local versions
  if (!newVersion || !currentVersion || currentVersion === 'local') {
    return false
  }

  // Remove 'v' prefix if present
  const newVer = newVersion.replace(/^v/, '')
  const curVer = currentVersion.replace(/^v/, '')

  // Split into parts and convert to numbers
  const newParts = newVer.split('.').map((n) => parseInt(n, 10) || 0)
  const curParts = curVer.split('.').map((n) => parseInt(n, 10) || 0)

  // Compare each part (major, minor, patch)
  for (let i = 0; i < Math.max(newParts.length, curParts.length); i++) {
    const newPart = newParts[i] || 0
    const curPart = curParts[i] || 0

    if (newPart > curPart) return true
    if (newPart < curPart) return false
  }

  return false // versions are equal
}

let notificationShown = false
export default function useVersionCheck() {
  const functions = useFunctions()
  const { token } = theme.useToken()

  const checkVersion = useCallback(async () => {
    if (notificationShown) {
      return
    }

    let version
    try {
      ;({ version } = await api(functions, 'app/version'))
    } catch {
      return
    }

    if (version !== appVersion && isNewerVersion(version, appVersion)) {
      message.open({
        key: 'new-version',
        icon: <UpCircleFilled style={{ color: token.colorInfo }} />,
        duration: 0,
        content: (
          <>
            {i18n.t('frontend:common.newVersionAvailable')}{' '}
            <Button
              size="small"
              type="link"
              onClick={() => {
                window.location.replace(
                  `${window.location.href}${window.location.href.includes('?') ? '&' : '?'}v=${version}`
                )
              }}
            >
              {i18n.t('frontend:common.refresh')}
            </Button>
          </>
        ),
      })
      notificationShown = true
    }
  }, [functions, token.colorInfo])

  useEffect(() => {
    checkVersion().then()
    const reloadInterval = setInterval(checkVersion, 15 * 60 * 1000) // 15 min
    return () => {
      clearInterval(reloadInterval)
    }
  }, [checkVersion])
}
